import { useEffect, useState, memo, useCallback, useContext } from "react";
import { Box } from "@mui/material";
import { CurrentUserContext } from "../components/context/RequireAuth";
import FlexPage from "../components/layout/FlexPage";
import {
  useListEvents,
  useRefreshCalendars,
  useGetAdvisorCalendarIntegrations,
  useAdvisorCalendarSearch,
} from "../services/calendar";
import {
  AdvisorCalendarSearchRequest,
  AdvisorCalendarSearchResponse,
  ListEventsRequest,
  ListEventsResponse_UserAttendeeStatus,
} from "protogen/calendar_service_pb";
import { defaultParametersV2, CalendarParametersV2 } from "types/calendars";
import { useListAdvisorFamilies } from "services/advisor";
import { EventNotice } from "protogen/calendar_pb";
import FayeCalendar from "components/calendar/FayeCalendar";
import {
  createColorMap,
  defaultColor,
  deduplicate,
  getWeekBoundaryInfo,
} from "components/calendar/utils";
import useIsMobile from "components/hooks/useIsMobile";
import { AdvisorCalendarTodos } from "components/calendar/CalendarTodos";
import {
  Family,
  FamilyStatus,
  ListAdvisorFamiliesResponse,
} from "protogen/advisors_service_pb";
import useDateParams from "components/hooks/calendar/useDateParams";
import useTitle from "components/hooks/useTitle";
import { useSearchParams } from "react-router-dom";
import TimezoneMismatchBanner from "components/calendar/TimezoneMismatchBanner";
import { useTimezoneMismatch } from "components/hooks/useTimezoneMismatch";

const ADVISOR_CALENDAR = "my-events";

const _activeFamilies = (families_?: Family[]) =>
  (families_ || []).filter(
    (f) =>
      f.status !== FamilyStatus.DEACTIVATED &&
      f.status !== FamilyStatus.DIGITAL_DEACTIVATED &&
      f.status !== FamilyStatus.DIGITAL_DEACTIVATED_TRIAL &&
      f.status !== FamilyStatus.DEACTIVATED_PROSPECT,
  );

const usePersistedFilteredFamilies = (): [
  Set<string> | null,
  (f: Set<string> | null) => void,
] => {
  const initialParams = new URLSearchParams(window.location.search);
  const [, setSearchParams] = useSearchParams();
  let initial = null;

  if (initialParams.get("calendars")) {
    const queryResults = new Set(
      (initialParams.get("calendars") || "").split(",").filter((s) => s),
    );
    initial = queryResults.size ? queryResults : null;
  }
  const [filteredFamilies, setFilteredFamilies] = useState<Set<string> | null>(
    initial,
  );
  useEffect(() => {
    setSearchParams(
      filteredFamilies && filteredFamilies.size > 0
        ? {
            calendars: Array.from(filteredFamilies).join(","),
          }
        : {},
    );
  }, [filteredFamilies]);
  return [filteredFamilies, setFilteredFamilies];
};

const _Wrapped = memo(
  ({
    familiesData,
    events,
    familyColorMap,
    params,
    fetchEvents,
    loading,
    refreshCalendar,
    userAttendeeStatuses,
    advisorCalendarSearch,
  }: {
    familiesData: ListAdvisorFamiliesResponse | null;
    events: EventNotice[];
    familyColorMap: Record<string, [string, string]> | null;
    params: CalendarParametersV2;
    fetchEvents: (
      newDate: Date,
      families?: Family[],
      includeDuplicates?: boolean,
    ) => Promise<void>;
    loading: boolean;
    refreshCalendar: () => Promise<void>;
    userAttendeeStatuses: ListEventsResponse_UserAttendeeStatus[];
    advisorCalendarSearch: (
      request: AdvisorCalendarSearchRequest,
    ) => Promise<AdvisorCalendarSearchResponse | null | undefined>;
  }) => {
    const isMobile = useIsMobile();
    const [filteredFamilies, setFilteredFamilies] =
      usePersistedFilteredFamilies();

    const { showMismatch, browserTimezone, userTimezone } =
      useTimezoneMismatch();

    const currentUser = useContext(CurrentUserContext);
    const [viewTimezone, setViewTimezone] = useState<string>(
      currentUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const [secondaryTimezone, setSecondaryTimezone] = useState<string>("");
    const [dualTimezoneEnabled, setDualTimezoneEnabled] =
      useState<boolean>(false);
    const attendingEventRefs = new Set(
      userAttendeeStatuses.map((status) => status.eventRef),
    );

    const handleTimezoneChange = (timezone: string) => {
      setViewTimezone(timezone);
    };

    const handleDualTimezoneChange = (
      dualEnabled: boolean,
      secondaryTz?: string,
    ) => {
      setDualTimezoneEnabled(dualEnabled);
      setSecondaryTimezone(secondaryTz || "");
    };

    return (
      <FlexPage
        leftAligned
        fullHeight
        headerPadding={isMobile}
        sx={{
          padding: isMobile ? "16px 6px 0" : "16px 32px 0",
          maxWidth: "1400px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <AdvisorCalendarTodos />
          <TimezoneMismatchBanner
            show={showMismatch}
            browserTimezone={browserTimezone}
            userTimezone={userTimezone}
            dismissible={false}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "10px",
              flexGrow: 1,
            }}
          >
            <FayeCalendar
              accountType="advisor"
              families={familiesData?.families}
              events={events.filter((e) => {
                if (filteredFamilies === null) {
                  return true;
                }
                return (
                  filteredFamilies.has(e.familyRef) ||
                  ((e.externalAdvisorCalendarRef ||
                    // Handle faye owned non-family events.
                    !e.familyRef ||
                    // Handle attending events.
                    attendingEventRefs.has(e.eventRef)) &&
                    filteredFamilies.has(ADVISOR_CALENDAR))
                );
              })}
              calendarFilters={[
                ..._activeFamilies(familiesData?.families)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((f) => ({
                    key: f.ref,
                    name: f.name,
                    color: familyColorMap?.[f.ref][0],
                    defaultUnchecked:
                      filteredFamilies !== null && !filteredFamilies.has(f.ref),
                  })),
                // Handle faye owned non-family events.
                {
                  name: "My Calendar",
                  key: ADVISOR_CALENDAR,
                  defaultUnchecked:
                    filteredFamilies !== null &&
                    !filteredFamilies.has(ADVISOR_CALENDAR),
                },
                // When we have a lot of families, add a quick filter for only my calendar.
                ...(_activeFamilies(familiesData?.families).length > 3
                  ? [
                      {
                        // add divider, flg as mutually exclusive.
                        name: "Show only my calendar",
                        key: "only_my_calendar",
                        action: () => {
                          return [ADVISOR_CALENDAR];
                        },
                      },
                    ]
                  : []),
              ]}
              setEnabledFilters={(filters) => {
                setFilteredFamilies(new Set(filters.map((f) => f.key)));
              }}
              getEventStyles={(event) => {
                const colors = familyColorMap?.[event.familyRef];
                return {
                  backgroundColor: colors?.[0] || defaultColor[0],
                  color: colors?.[1] || defaultColor[1],
                };
              }}
              refreshEvents={async (includeDuplicates) => {
                fetchEvents(
                  new Date(params.year, params.month - 1, params.day),
                  undefined,
                  includeDuplicates,
                );
              }}
              refreshCalendar={async () => {
                await refreshCalendar();
              }}
              handleNavigate={async (d) => {
                fetchEvents(d);
              }}
              loading={loading}
              viewTimezone={viewTimezone}
              onTimezoneChange={handleTimezoneChange}
              secondaryTimezone={secondaryTimezone}
              dualTimezoneEnabled={dualTimezoneEnabled}
              onDualTimezoneChange={handleDualTimezoneChange}
              searchEvents={async (query) => {
                const activeFamilies = _activeFamilies(familiesData?.families);
                const familyRefs =
                  filteredFamilies === null
                    ? activeFamilies.map((family) => family.ref)
                    : activeFamilies
                        .filter((family) => filteredFamilies.has(family.ref))
                        .map((family) => family.ref);
                const includeAdvisorEvents =
                  filteredFamilies === null ||
                  filteredFamilies.has(ADVISOR_CALENDAR);
                const response = await advisorCalendarSearch(
                  new AdvisorCalendarSearchRequest({
                    query,
                    familyRefs,
                    includeAdvisorEvents,
                    limit: 100,
                  }),
                );
                return response?.eventNotices || [];
              }}
            />
          </Box>
        </Box>
      </FlexPage>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.events === nextProps.events &&
      prevProps.loading === nextProps.loading
    );
  },
);

export default () => {
  useTitle("Calendar");
  const defaultDate = useDateParams();
  const [events, setEvents] = useState<EventNotice[]>([]);
  const [params, setParams] = useState<CalendarParametersV2>({
    ...defaultParametersV2(),
  });
  const [userAttendeeStatuses, setUserAttendeeStatuses] = useState<
    ListEventsResponse_UserAttendeeStatus[]
  >([]);

  const { request: refreshRequest } = useRefreshCalendars();
  const { data: familiesData, request: familiesRequest } =
    useListAdvisorFamilies();
  const { request: getIntegrations } = useGetAdvisorCalendarIntegrations();
  const { request: advisorCalendarSearchRequest } = useAdvisorCalendarSearch();

  const { request: eventsRequest, loading } = useListEvents();
  const [familyColorMap, setFamilyColorMap] = useState<Record<
    string,
    [string, string]
  > | null>(null);
  const onLoad = async () => {
    const families = _activeFamilies((await familiesRequest())?.families || []);
    if (!familyColorMap) {
      setFamilyColorMap(createColorMap(families.map((f) => f.ref)));
    }
    getIntegrations();
    // For the first request, pass in families.  Using the data or setState is async
    // and will be null when we make this request
    fetchEvents(defaultDate, families);
  };
  useEffect(() => {
    onLoad();
  }, []);
  const fetchEvents = useCallback(
    async (newDate: Date, families?: Family[], includeDuplicates = false) => {
      const familyData = families || _activeFamilies(familiesData?.families);
      const { currentMonth, currentYear, otherMonth, otherYear } =
        getWeekBoundaryInfo(newDate);

      setParams({
        ...params,
        month: currentMonth,
        year: currentYear,
        day: newDate.getDate(),
      });

      const requests = [
        eventsRequest(
          new ListEventsRequest({
            ...params,
            familyRefs: familyData.map((f) => f.ref),
            month: currentMonth,
            year: currentYear,
            includeExternalEvents: true,
            includeAdvisorEvents: true,
            forceIncludeUnreadEvents: true,
            includeDuplicateEvents: includeDuplicates,
          }),
        ),
      ];

      if (otherMonth !== null && otherYear !== null) {
        requests.push(
          eventsRequest(
            new ListEventsRequest({
              ...params,
              familyRefs: familyData.map((f) => f.ref),
              month: otherMonth,
              year: otherYear,
              includeExternalEvents: true,
              includeAdvisorEvents: true,
              includeOverdueTasks: false,
              includeDuplicateEvents: includeDuplicates,
            }),
          ),
        );
      }

      const results = await Promise.all(requests);
      // A basic dedupe on eventRef and startSec.  The FE requests month/year,
      // but the backend request an extra day window on each end to account for
      // timezone.  A more robust API that accepts more granular time or other improvements
      // can remove this dedupe.
      const allEvents = deduplicate(
        // @ts-ignore
        results.flatMap((result) => result?.eventNotices || []),
        ["eventRef", "startSec"],
      );
      const allUserAttendeeStatuses = deduplicate(
        results.flatMap((result) => result?.userAttendeeStatuses || []),
        ["eventRef", "attendeeRef"],
      );

      setEvents(allEvents);
      setUserAttendeeStatuses(allUserAttendeeStatuses);
    },
    [eventsRequest, familiesData, params],
  );

  return (
    <_Wrapped
      familiesData={familiesData}
      events={events}
      familyColorMap={familyColorMap}
      params={params}
      fetchEvents={fetchEvents}
      loading={loading}
      refreshCalendar={async () => {
        await refreshRequest({
          familyRef: "",
        });
      }}
      userAttendeeStatuses={userAttendeeStatuses}
      advisorCalendarSearch={advisorCalendarSearchRequest}
    />
  );
};
